import type { ProjectDocumentV1 } from '../../contracts/src/index.ts';

import { PdfEngineError } from './errors.ts';
import {
  PDF_SCENE_SCHEMA_VERSION,
  type PdfSceneV1,
  type SceneFont,
  type SceneNode,
  type SceneTransformMm,
} from './types.ts';

function finite(value: number): boolean {
  return typeof value === 'number' && Number.isFinite(value);
}

function validateTransform(transform: SceneTransformMm, path: string, issues: string[]): void {
  if (!finite(transform.cxMm)) issues.push(path + '.cxMm debe ser finito');
  if (!finite(transform.cyMm)) issues.push(path + '.cyMm debe ser finito');
  if (!finite(transform.widthMm) || transform.widthMm <= 0) issues.push(path + '.widthMm debe ser positivo');
  if (!finite(transform.heightMm) || transform.heightMm <= 0) issues.push(path + '.heightMm debe ser positivo');
  if (!finite(transform.scale) || transform.scale <= 0) issues.push(path + '.scale debe ser positivo');
  if (!finite(transform.rotationDeg)) issues.push(path + '.rotationDeg debe ser finito');
}

export function validatePdfScene(scene: PdfSceneV1): readonly string[] {
  const issues: string[] = [];
  if (scene.schemaVersion !== PDF_SCENE_SCHEMA_VERSION) issues.push('schemaVersion debe ser 1');
  if (!scene.documentId.trim()) issues.push('documentId no puede estar vacio');
  if (!finite(scene.widthMm) || scene.widthMm <= 0) issues.push('widthMm debe ser positivo');
  if (!finite(scene.heightMm) || scene.heightMm <= 0) issues.push('heightMm debe ser positivo');

  if (scene.product.kind === 'tube') {
    const product = scene.product;
    if (Math.abs(product.developmentEffectiveMm - (product.developmentBaseMm + product.materialIncrementMm)) > 1e-9) {
      issues.push('developmentEffectiveMm debe ser base + incremento');
    }
    if (Math.abs(product.materialIncrementMm - product.extensionPerSideMm * 2) > 1e-9) {
      issues.push('extensionPerSideMm debe distribuir el incremento en dos lados');
    }
    if (Math.abs(scene.widthMm - product.developmentEffectiveMm) > 1e-9) {
      issues.push('widthMm debe usar developmentEffectiveMm');
    }
    if (Math.abs(scene.heightMm - product.heightMm) > 1e-9) {
      issues.push('heightMm debe coincidir con el alto del tubo');
    }
  } else {
    if (Math.abs(scene.widthMm - scene.product.widthMm) > 1e-9) {
      issues.push('widthMm debe coincidir con el ancho del Sticker');
    }
    if (Math.abs(scene.heightMm - scene.product.heightMm) > 1e-9) {
      issues.push('heightMm debe coincidir con el alto del Sticker');
    }
    if (scene.product.cutLineRule !== 'v46-300dpi') {
      issues.push('cutLineRule debe ser v46-300dpi');
    }
  }

  if (scene.background.kind === 'asset') {
    if (!scene.background.assetId.trim()) issues.push('background.assetId no puede estar vacio');
    if (scene.background.transform) validateTransform(scene.background.transform, 'background.transform', issues);
  }

  const ids = new Set<string>();
  scene.nodes.forEach((node, index) => {
    const path = 'nodes[' + String(index) + ']';
    if (!node.id.trim()) issues.push(path + '.id no puede estar vacio');
    if (ids.has(node.id)) issues.push(path + '.id esta duplicado');
    ids.add(node.id);
    validateTransform(node.transform, path + '.transform', issues);
    if (node.type === 'asset' && !node.assetId.trim()) issues.push(path + '.assetId no puede estar vacio');
    if (node.type === 'text') {
      if (!finite(node.fontSizeMm) || node.fontSizeMm <= 0) issues.push(path + '.fontSizeMm debe ser positivo');
      if (node.font.kind === 'embedded' && !node.font.fontAssetId.trim()) {
        issues.push(path + '.font.fontAssetId no puede estar vacio');
      }
      if (node.stroke && (!finite(node.stroke.widthMm) || node.stroke.widthMm < 0)) {
        issues.push(path + '.stroke.widthMm debe ser mayor o igual a cero');
      }
    }
    if (node.opacity !== undefined && (!finite(node.opacity) || node.opacity < 0 || node.opacity > 1)) {
      issues.push(path + '.opacity debe estar entre 0 y 1');
    }
  });
  return issues;
}

export function assertPdfScene(scene: PdfSceneV1): void {
  const issues = validatePdfScene(scene);
  if (issues.length) {
    throw new PdfEngineError('PDF_SCENE_INVALID', 'La escena PDF no cumple el contrato fisico.', {
      issues,
    });
  }
}

export interface ProjectDocumentSceneOptions {
  readonly resolveCatalogAssetId: (patternId: string) => string | null;
  readonly resolveFont: (fontFamily: string) => SceneFont | null;
}

export function buildPdfSceneFromProjectDocument(
  document: ProjectDocumentV1,
  options: ProjectDocumentSceneOptions,
): PdfSceneV1 {
  const product = document.product.kind === 'tube'
    ? { ...document.product }
    : {
        kind: 'sticker' as const,
        shape: document.product.shape,
        sourceLabel: document.product.source.kind === 'preset'
          ? document.product.source.presetCode
          : 'personalizado',
        widthMm: document.product.widthMm,
        heightMm: document.product.heightMm,
        cutLineRule: 'v46-300dpi' as const,
      };

  const background: PdfSceneV1['background'] = document.background.kind === 'white'
    ? { kind: 'white' }
    : document.background.kind === 'catalog'
      ? (() => {
          const assetId = options.resolveCatalogAssetId(document.background.patternId);
          if (!assetId) {
            throw new PdfEngineError('PDF_ASSET_MISSING', 'El fondo de catalogo no tiene asset asociado.', {
              patternId: document.background.patternId,
            });
          }
          return { kind: 'asset' as const, assetId, fit: 'cover' as const };
        })()
      : {
          kind: 'asset',
          assetId: document.background.assetId,
          fit: 'cover',
          transform: document.background.transform,
        };

  const nodes: SceneNode[] = document.nodes.map((node) => {
    if (node.type === 'image') {
      return {
        id: node.id,
        type: 'asset',
        assetId: node.assetId,
        transform: node.transform,
      };
    }
    const font = options.resolveFont(node.fontFamily);
    if (!font) {
      throw new PdfEngineError(
        'PDF_FONT_BINDING_REQUIRED',
        'La tipografia necesita una vinculacion explicita con bytes embebibles.',
        { fontFamily: node.fontFamily, nodeId: node.id },
      );
    }
    return {
      id: node.id,
      type: 'text',
      transform: node.transform,
      text: node.text,
      font,
      fontSizeMm: node.fontSizeMm,
      fillColor: node.fillColor,
      stroke: node.stroke,
    };
  });

  const scene: PdfSceneV1 = {
    schemaVersion: PDF_SCENE_SCHEMA_VERSION,
    documentId: document.projectId,
    product,
    widthMm: document.product.kind === 'tube'
      ? document.product.developmentEffectiveMm
      : document.product.widthMm,
    heightMm: document.product.kind === 'tube'
      ? document.product.heightMm
      : document.product.heightMm,
    background,
    nodes,
  };
  assertPdfScene(scene);
  return scene;
}

export function serializePdfScene(scene: PdfSceneV1): string {
  assertPdfScene(scene);
  return JSON.stringify(scene);
}

export function deserializePdfScene(serialized: string): PdfSceneV1 {
  const parsed = JSON.parse(serialized) as PdfSceneV1;
  assertPdfScene(parsed);
  return parsed;
}
